import type { CVListItemDto } from '@cibli/shared';

import { differenceInCalendarDays } from '@cibli/shared';
import { Check, Eye, FileUser } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useCVList } from '@/features/cv/hooks/useCVList';
import { getInterviewUrl } from '@/lib/interview-url';

interface CVFilterSidebarProps {
  activeCvId: string | null;
  onToggleCv: (cvId: string) => void;
}

export function CVFilterSidebar({ activeCvId, onToggleCv }: CVFilterSidebarProps) {
  const { t } = useTranslation();
  const { cvs, isLoading } = useCVList();

  const handleViewCv = (e: React.MouseEvent, cvId: string) => {
    e.stopPropagation();
    const url = new URL(getInterviewUrl());
    url.searchParams.set('cvId', cvId);
    url.searchParams.set('source', 'Dashboard-Jobs');
    window.open(url.toString(), '_blank');
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-3 px-1">
        <h3 className="font-display text-base text-cj-green-dark">
          {t('dashboard.layout.navigation.cvs')}
        </h3>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="w-5 h-5 border-2 border-cj-green/30 border-t-cj-green rounded-full animate-spin" />
        </div>
      ) : cvs.length === 0 ? (
        <div className="pro-card p-4 text-center">
          <p className="text-xs text-cj-green-dark/40">{t('dashboard.cvList.empty.title')}</p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto space-y-2 pr-1 scrollbar-hide">
          {cvs.map(cv => (
            <CvListItem
              key={cv.id}
              cv={cv}
              isActive={activeCvId === cv.id}
              onToggle={() => onToggleCv(cv.id)}
              onView={e => handleViewCv(e, cv.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CvListItem({
  cv,
  isActive,
  onToggle,
  onView,
}: {
  cv: CVListItemDto;
  isActive: boolean;
  onToggle: () => void;
  onView: (e: React.MouseEvent) => void;
}) {
  const { t } = useTranslation();
  const date = new Date(cv.updatedAt);
  const diffDays = differenceInCalendarDays(new Date(), date);
  const dateLabel =
    diffDays === 0
      ? t('dashboard.cvList.card.today')
      : diffDays === 1
        ? t('dashboard.cvList.card.yesterday')
        : diffDays < 7
          ? t('dashboard.cvList.card.daysAgo', { days: diffDays })
          : t('dashboard.cvList.card.weeksAgo', { weeks: Math.floor(diffDays / 7) });

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      className={`w-full text-left pro-card p-6 transition cursor-pointer ${
        isActive ? 'ring-2 ring-cj-green bg-cj-green/5' : 'hover:bg-cj-green-light/10'
      }`}
    >
      <div className="flex items-start gap-2.5">
        <div className="w-9 h-9 rounded-lg bg-cj-green-light/50 flex items-center justify-center flex-shrink-0">
          {isActive ? (
            <Check className="w-4 h-4 text-cj-green" />
          ) : (
            <FileUser className="w-4.5 h-4.5 text-cj-green" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-xs text-cj-green-dark truncate">{cv.jobTitle}</h4>
          <p className="text-[10px] text-cj-green-dark/40 mt-0.5">{dateLabel}</p>
        </div>
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            onView(e);
          }}
          className="flex items-center gap-1 text-[10px] font-semibold text-cj-green-nuance hover:text-cj-green transition flex-shrink-0 self-center"
          title={t('dashboard.jobs.viewTitle')}
        >
          <Eye className="w-3.5 h-3.5" />
          {t('dashboard.jobs.view')}
        </button>
      </div>
    </div>
  );
}
